import { Component } from '@angular/core';
import { AppConfig } from '../../../config/appConfig';

interface ComparisonRow {
  label: string;
  yachtapp: 'check' | 'partial' | 'no';
  spreadsheet: 'check' | 'partial' | 'no';
  manual: 'check' | 'partial' | 'no';
}

interface FeaturePillar {
  icon: string;
  title: string;
  description: string;
  bullets: string[];
}

@Component({
  selector: 'app-features',
  templateUrl: './features.component.html',
  styleUrl: './features.component.css'
})
export class FeaturesComponent {
  title = AppConfig.title;

  readonly comparisonRows: ComparisonRow[] = [
    { label: 'Multi-boat and multi-account operations', yachtapp: 'check', spreadsheet: 'partial', manual: 'no' },
    { label: 'Worklog history with documents and audit trail', yachtapp: 'check', spreadsheet: 'partial', manual: 'no' },
    { label: 'Checklist workflows with expiration tracking and PDF exports', yachtapp: 'check', spreadsheet: 'no', manual: 'no' },
    { label: 'Inventory, warranty, and expiry monitoring', yachtapp: 'check', spreadsheet: 'partial', manual: 'no' },
    { label: 'Cruise planning with crew, passenger, and IMO list support', yachtapp: 'check', spreadsheet: 'partial', manual: 'no' },
    { label: 'Saved routes, nautical mapping, and live navigation support', yachtapp: 'check', spreadsheet: 'no', manual: 'no' },
    { label: 'Automated reminders and operational notifications', yachtapp: 'check', spreadsheet: 'partial', manual: 'no' },
    { label: 'Role-based collaboration and invite workflows', yachtapp: 'check', spreadsheet: 'no', manual: 'no' },
    { label: 'AI-assisted route analysis', yachtapp: 'check', spreadsheet: 'no', manual: 'no' },
    { label: 'Cloud sync and account-wide visibility', yachtapp: 'check', spreadsheet: 'partial', manual: 'no' }
  ];

  readonly pillars: FeaturePillar[] = [
    {
      icon: 'bi bi-speedometer2',
      title: 'Operational command center',
      description: `${this.title} turns fragmented vessel activity into one disciplined operating picture. Your dashboard surfaces boats, maintenance pressure, reminders, routes, documents, cruises, and activity without forcing your team to hunt through disconnected tools.`,
      bullets: [
        'Reorderable dashboard widgets tailored to the way your operation runs.',
        'Boat, maintenance, reminder, route, cruise, and document visibility in one place.',
        'Subscription-aware workspace that scales from owner-operator to fleet workflows.'
      ]
    },
    {
      icon: 'bi bi-tools',
      title: 'Maintenance discipline without spreadsheet fatigue',
      description: `From service history to recurring plans, the platform is designed to reduce forgotten work, undocumented fixes, and expensive surprises. Teams can log work, attach evidence, generate maintenance plans, and follow checklists with completion history.`,
      bullets: [
        'Worklogs with due dates, costs, history, and supporting files.',
        'Boat and cruise checklists with runs, expirations, resets, and PDF exports.',
        'Maintenance-plan generation grounded in each boat profile.'
      ]
    },
    {
      icon: 'bi bi-compass',
      title: 'Cruise intelligence and route confidence',
      description: `Planning is no longer separate from execution. Build cruises, assign people, save routes, work from nautical mapping tools, and activate live navigation tracking when the trip begins.`,
      bullets: [
        'Cruise planning with crew, passengers, people lists, and reminders.',
        'Saved routes, measurement tools, seamarks, and bathymetry overlays.',
        'Live track points plus AI-assisted route analysis for sharper decision making.'
      ]
    },
    {
      icon: 'bi bi-people',
      title: 'Controlled collaboration for serious operations',
      description: `Yacht operations break down when information is trapped in one person’s notebook. ${this.title} gives owners and teams structured collaboration through account membership, role-based permissions, invites, and shared operational context.`,
      bullets: [
        'Invite members, control access, and tune permissions by role.',
        'Manage onboard people, passenger/crew manifests, and linked account users.',
        'Automated notifications for due work, inventory risk, and key account events.'
      ]
    }
  ];

  iconClass(state: ComparisonRow['yachtapp']): string {
    switch (state) {
      case 'check':
        return 'bi bi-check-circle-fill text-success';
      case 'partial':
        return 'bi bi-dash-circle-fill text-warning';
      default:
        return 'bi bi-x-circle-fill text-danger';
    }
  }
}
